import type { StateCreator } from 'zustand';
import type { AppState } from '../types';
import { createEmptyProfile, extractProfileData } from '../constants';
import type { UserRole, Profile, ProfileSummary, AthleteProfile } from '@/types';
import { createLogger } from '@/lib/logger';

const log = createLogger('profile-slice');

function getFallbackDisplayName(email: string): string {
    const localPart = email.split('@')[0]?.trim();
    return localPart && localPart.length > 0 ? localPart : 'Me';
}

export interface ProfileSlice {

    userRole: UserRole;
    profiles: Profile[];
    activeProfileId: string | null;

    setUserRole: (role: UserRole) => void;
    getProfileSummaries: () => ProfileSummary[];
    switchProfile: (profileId: string) => void;
    authUser: { id: string; email: string } | null;
    startImpersonation: (userId: string) => Promise<void>;
    stopImpersonation: () => Promise<void>;
    setAuthUser: (user: { id: string; email: string } | null) => void;
    isImpersonating: boolean;
    impersonatedUserId: string | null;
    isSupportImpersonation: boolean;
    addCoachedProfile: (intervalsAthleteId: string, displayName: string, sport: 'Running' | 'Cycling' | 'Triathlon', options?: { explicitId?: string, inviteToken?: string, coachId?: string, coachName?: string }) => string;
    createSelfProfile: (intervalsAthleteId: string, displayName: string, options?: { setActive?: boolean }) => string;
    removeProfile: (profileId: string) => void;
    updateProfileDisplayName: (profileId: string, displayName: string) => void;
    updateProfileAthlete: (profileId: string, athleteData: Partial<AthleteProfile>) => void;
    checkAdminPromotion: (email: string) => void;
    regenerateInvite: (profileId: string) => string;
    updateProfileIntervalsId: (profileId: string, intervalsId: string) => Promise<void>;
}

export const createProfileSlice: StateCreator<AppState, [], [], ProfileSlice> = (set, get) => ({

    userRole: 'athlete' as UserRole,
    profiles: [],
    activeProfileId: null,
    isImpersonating: false,
    impersonatedUserId: null,
    isSupportImpersonation: false,
    authUser: null,

    setAuthUser: (user) => set((state) => {
        // Heal stale "self impersonation" states where the impersonated target
        // equals the authenticated user. This can happen if an admin clicks
        // "view" on their own profile entry.
        if (user && state.isImpersonating && state.impersonatedUserId === user.id) {
            return {
                authUser: user,
                isImpersonating: false,
                impersonatedUserId: null,
                isSupportImpersonation: false,
                activeProfileId: user.id,
            };
        }

        if (!user) {
            return { authUser: null };
        }

        const existingProfile = state.profiles.find((profile) => profile.id === user.id);
        const fallbackDisplayName = state.athlete.name || getFallbackDisplayName(user.email);
        const shouldActivateSelfProfile = !state.isImpersonating && !state.activeProfileId;

        if (existingProfile) {
            const shouldConvertToSelf = existingProfile.relation !== 'self';

            return {
                authUser: user,
                profiles: shouldConvertToSelf
                    ? state.profiles.map((profile) =>
                        profile.id === user.id
                            ? {
                                ...profile,
                                relation: 'self',
                                displayName: profile.displayName || fallbackDisplayName,
                                athlete: {
                                    ...profile.athlete,
                                    name: profile.athlete.name || fallbackDisplayName,
                                },
                            }
                            : profile
                    )
                    : state.profiles,
                activeProfileId: shouldActivateSelfProfile ? user.id : state.activeProfileId,
            };
        }

        const placeholderProfile = createEmptyProfile(
            user.id,
            state.intervals.athleteId || '',
            fallbackDisplayName,
            'self',
            state.athlete.sport
        );

        placeholderProfile.athlete = {
            ...placeholderProfile.athlete,
            name: fallbackDisplayName,
            sport: state.athlete.sport,
            profilePicture: state.athlete.profilePicture,
        };

        return {
            authUser: user,
            profiles: [...state.profiles, placeholderProfile],
            activeProfileId: shouldActivateSelfProfile ? user.id : state.activeProfileId,
        };
    }),



    setUserRole: (role) => set({ userRole: role }),

    startImpersonation: async (userId: string) => {
        const state = get();
        const authUserId = state.authUser?.id;
        const targetProfile = state.profiles.find(p => p.id === userId);

        // Guardrail: never allow "impersonating yourself". It creates false
        // impersonation banners and can interfere with persistence behavior.
        if ((authUserId && userId === authUserId) || targetProfile?.relation === 'self') {
            set({
                isImpersonating: false,
                impersonatedUserId: null,
                isSupportImpersonation: false,
                activeProfileId: authUserId || userId,
            });
            return;
        }

        // 1. SYNC CURRENT STATE (Admin) TO PROFILES before switching away
        // This prevents "Ghost State" (data loss) when switching back to Admin later.
        if (state.activeProfileId) {
            const updatedProfiles = state.profiles.map(p => {
                if (p.id === state.activeProfileId) {
                    return {
                        ...p,
                        athlete: state.athlete,
                        metrics: state.metrics,
                        goal: state.goal,
                        events: state.events,
                        availability: state.availability,
                        longRunDay: state.longRunDay,
                        plan: state.plan,
                        planState: state.planState,
                        planName: state.planName,
                        planHistory: state.planHistory,
                        blocks: state.blocks,
                        config: state.config,
                        progression: state.progression,
                        cyclingProgression: state.cyclingProgression,
                        planSettings: state.planSettings,
                        triathlonProgression: state.triathlonProgression,
                        gamification: state.gamification,
                    };
                }
                return p;
            });
            set({ profiles: updatedProfiles });
        }

        // Check if user is already in our profiles
        // (Re-get state after potential update, though 'profiles' is ref)
        const existing = get().profiles.find(p => p.id === userId);

        if (!existing) {
            // Add a temporary profile so switching works and hydration doesn't overwrite 'self'.
            // Note: We use 'coached' relation for impersonation so the sync logic targetUserId identifies correctly.
            state.addCoachedProfile('pending', 'Impersonating User', 'Running', { explicitId: userId });
        }

        // Apply the target's snapshot immediately so the UI never shows the
        // admin's own data under the impersonated identity, and flag hydration
        // so route guards wait for the cloud download instead of redirecting.
        const targetSnapshot = get().profiles.find(p => p.id === userId);
        set({
            isImpersonating: true,
            impersonatedUserId: userId,
            isSupportImpersonation: true,
            activeProfileId: userId,
            ...(targetSnapshot ? extractProfileData(targetSnapshot) : {}),
            intervals: {
                ...get().intervals,
                athleteId: targetSnapshot?.intervalsAthleteId || '',
            },
            isHydrating: true,
        });

        try {
            const { downloadCloudDataToLocal } = await import('@/services/cloudSync');
            // Non-interactive because admin knows what they are doing
            await downloadCloudDataToLocal(userId, false);
        } catch (error) {
            log.error('startImpersonation', 'Failed to hydrate impersonated profile', {
                userId,
                error: error instanceof Error ? error.message : String(error),
            });
            set({ isHydrating: false });
        }
    },

    stopImpersonation: async () => {
        const { supabase } = await import('@/lib/supabase');
        const { downloadCloudDataToLocal } = await import('@/services/cloudSync');

        // 1. SYNC CURRENT STATE (Impersonated User) TO PROFILES before switching away
        const state = get();
        if (state.activeProfileId) {
            const updatedProfiles = state.profiles.map(p => {
                if (p.id === state.activeProfileId) {
                    return {
                        ...p,
                        athlete: state.athlete,
                        metrics: state.metrics,
                        goal: state.goal,
                        events: state.events,
                        availability: state.availability,
                        longRunDay: state.longRunDay,
                        plan: state.plan,
                        planState: state.planState,
                        planName: state.planName,
                        planHistory: state.planHistory,
                        blocks: state.blocks,
                        config: state.config,
                        progression: state.progression,
                        cyclingProgression: state.cyclingProgression,
                        planSettings: state.planSettings,
                        triathlonProgression: state.triathlonProgression,
                        gamification: state.gamification,
                    };
                }
                return p;
            });
            set({ profiles: updatedProfiles });
        }

        const { data: { user } } = await supabase.auth.getUser();

        // Restore the admin's own snapshot immediately so the impersonated
        // user's data never lingers on screen while the cloud refresh runs.
        const refreshed = get();
        const selfSnapshot = user
            ? refreshed.profiles.find(p => p.id === user.id) ?? refreshed.profiles.find(p => p.relation === 'self')
            : undefined;

        set({
            isImpersonating: false,
            impersonatedUserId: null,
            isSupportImpersonation: false,
            activeProfileId: user?.id || null,
            ...(selfSnapshot ? extractProfileData(selfSnapshot) : {}),
            ...(selfSnapshot ? {
                intervals: {
                    ...refreshed.intervals,
                    athleteId: selfSnapshot.intervalsAthleteId,
                },
            } : {}),
            ...(user ? { isHydrating: true } : {}),
        });

        if (user) {
            try {
                await downloadCloudDataToLocal(user.id, false);
            } catch (error) {
                log.error('stopImpersonation', 'Failed to re-hydrate own profile', {
                    userId: user.id,
                    error: error instanceof Error ? error.message : String(error),
                });
                set({ isHydrating: false });
            }
        }
    },

    getProfileSummaries: () => {
        const state = get();
        return state.profiles.map(p => ({
            id: p.id,
            intervalsAthleteId: p.intervalsAthleteId,
            displayName: p.displayName,
            relation: p.relation,
            sport: p.athlete.sport,
            lastAccessedAt: p.lastAccessedAt,
            profilePicture: p.athlete.profilePicture,
            isSupporter: p.athlete.isSupporter,
            inviteToken: p.inviteToken,
            connectedAt: p.connectedAt,
        })).sort((a, b) => {
            if (a.relation === 'self' && b.relation !== 'self') return -1;
            if (b.relation === 'self' && a.relation !== 'self') return 1;
            return new Date(b.lastAccessedAt).getTime() - new Date(a.lastAccessedAt).getTime();
        });
    },

    switchProfile: (profileId) => {
        const state = get();
        const profile = state.profiles.find(p => p.id === profileId);
        if (!profile) return;

        const isCoached = profile.relation === 'coached';
        const hasHydratedSnapshot = !isCoached || profile.isHydrated === true;

        // Log profile switch for debugging
        log.info('switchProfile', `Switching to ${profile.relation} profile: ${profile.displayName}`, {
            profileId, isCoached,
        });

        // 1. SYNC CURRENT STATE TO PROFILES before switching
        let updatedProfiles = state.profiles;
        if (state.activeProfileId) {
            updatedProfiles = state.profiles.map(p => {
                if (p.id === state.activeProfileId) {
                    return {
                        ...p,
                        athlete: state.athlete,
                        metrics: state.metrics,
                        goal: state.goal,
                        events: state.events,
                        availability: state.availability,
                        longRunDay: state.longRunDay,
                        plan: state.plan,
                        planState: state.planState,
                        planName: state.planName,
                        planHistory: state.planHistory,
                        blocks: state.blocks,
                        config: state.config,
                        progression: state.progression,
                        cyclingProgression: state.cyclingProgression,
                        planSettings: state.planSettings,
                        triathlonProgression: state.triathlonProgression,
                        gamification: state.gamification,
                    };
                }
                return p;
            });
        }

        // 2. Update Last Accessed for Target Profile
        updatedProfiles = updatedProfiles.map(p =>
            p.id === profileId
                ? { ...p, lastAccessedAt: new Date().toISOString() }
                : p
        );

        // Always apply the target profile's snapshot — even an unhydrated
        // coached profile carries a placeholder athlete (name = displayName).
        // Skipping this left the PREVIOUS profile's data on screen until the
        // async cloud download finished, which surfaced as stale plans and
        // spurious /onboarding redirects when the leftover snapshot was empty.
        const targetProfile = updatedProfiles.find(p => p.id === profileId) ?? profile;

        set({
            profiles: updatedProfiles,
            activeProfileId: profileId,
            ...extractProfileData(targetProfile),
            intervals: {
                // Preserve existing credentials (accessToken, refreshToken) from the authenticated user
                ...state.intervals,
                // Update the target athlete ID to the selected profile
                athleteId: profile.intervalsAthleteId,
            },
            // Coached profiles still use the impersonation routing path for
            // athlete-scoped edge functions, but they are not read-only
            // support impersonation sessions.
            isImpersonating: isCoached,
            impersonatedUserId: isCoached ? profileId : null,
            isSupportImpersonation: false,
            // Gate route guards while the cloud download runs; clear the gate
            // when the snapshot is already complete so an interrupted earlier
            // download can never leave the app stuck in a loading state.
            isHydrating: !hasHydratedSnapshot,
        });

        // Async: download fresh cloud data for coached profiles (mirrors startImpersonation)
        if (isCoached) {
            import('@/services/cloudSync').then(({ downloadCloudDataToLocal }) => {
                downloadCloudDataToLocal(profileId, false);
            }).catch((error) => {
                log.error('switchProfile', 'Failed to hydrate coached profile after switch', {
                    profileId,
                    error: error instanceof Error ? error.message : String(error),
                });
                set({ isHydrating: false });
            });
        }
    },

    addCoachedProfile: (intervalsAthleteId, displayName, sport, options = {}) => {
        const id = options.explicitId || crypto.randomUUID();
        const existing = get().profiles.find(p => p.id === id);

        if (existing) {
            // Never mutate an existing self profile into coached relation.
            if (existing.relation === 'self') {
                return existing.id;
            }

            set((state) => ({
                profiles: state.profiles.map(p => {
                    if (p.id !== id || p.relation !== 'coached') return p;
                    return {
                        ...p,
                        displayName: displayName || p.displayName,
                        intervalsAthleteId: intervalsAthleteId || p.intervalsAthleteId,
                        coachId: options.coachId ?? p.coachId,
                        coachName: options.coachName ?? p.coachName,
                        inviteToken: options.inviteToken ?? p.inviteToken,
                    };
                }),
            }));

            return id;
        }

        const profile = createEmptyProfile(id, intervalsAthleteId, displayName, 'coached', sport);

        // Add invitation metadata
        profile.inviteToken = options.inviteToken;
        profile.coachId = options.coachId;
        profile.coachName = options.coachName;

        set((state) => ({
            profiles: [...state.profiles, profile],
        }));

        return id;
    },

    createSelfProfile: (intervalsAthleteId, displayName, options = { setActive: true }) => {
        const state = get();
        const existingSelf = state.profiles.find(p => p.relation === 'self');
        const authUserId = state.authUser?.id;
        const authProfile = authUserId
            ? state.profiles.find(p => p.id === authUserId)
            : undefined;

        // If the authenticated profile already exists as coached, convert it to self.
        if (authProfile && authProfile.relation === 'coached') {
            const authId = authProfile.id;
            set((state) => ({
                profiles: state.profiles.map(p =>
                    p.id === authId
                        ? {
                            ...p,
                            relation: 'self',
                            intervalsAthleteId,
                            displayName: displayName || p.displayName,
                            isHydrated: true,
                            athlete: { ...state.athlete, name: displayName || state.athlete.name },
                        }
                        : p
                ),
                activeProfileId: options.setActive ? authId : state.activeProfileId,
                intervals: {
                    ...state.intervals,
                    athleteId: intervalsAthleteId,
                },
            }));
            return authId;
        }

        if (existingSelf) {
            set((state) => ({
                profiles: state.profiles.map(p =>
                    p.relation === 'self'
                        ? { ...p, intervalsAthleteId, displayName, lastAccessedAt: new Date().toISOString(), isHydrated: true }
                        : p
                ),
            }));
            // Only switch if explicitly requested (default: true)
            if (options.setActive && state.activeProfileId !== existingSelf.id) {
                set({ activeProfileId: existingSelf.id });
            }
            return existingSelf.id;
        }

        // Use auth user's ID so the self profile ID matches the Supabase UUID.
        // This ensures activeProfileId (set during cloud hydration) stays consistent.
        const id = state.authUser?.id || crypto.randomUUID();
        const now = new Date().toISOString();

        set((state) => {
            // Create profile FROM current state to preserve all existing data.
            // Previously this used createEmptyProfile() + extractProfileData() which
            // wiped current plan, settings, ATP, etc. with empty defaults.
            const profileFromCurrentState: Profile = {
                id,
                intervalsAthleteId,
                relation: 'self',
                displayName,
                createdAt: now,
                lastAccessedAt: now,
                isHydrated: true,
                role: 'athlete',
                isPro: false,
                disabled: false,
                athlete: { ...state.athlete, name: displayName || state.athlete.name },
                metrics: state.metrics,
                goal: state.goal,
                events: state.events,
                availability: state.availability,
                longRunDay: state.longRunDay,
                plan: state.plan,
                planState: state.planState,
                planName: state.planName,
                planHistory: state.planHistory,
                blocks: state.blocks,
                config: state.config,
                progression: state.progression,
                cyclingProgression: state.cyclingProgression,
                planSettings: state.planSettings,
                triathlonProgression: state.triathlonProgression,
            };

            return {
                profiles: [...state.profiles, profileFromCurrentState],
                // Only set active profile if requested
                activeProfileId: options.setActive ? id : state.activeProfileId,
                // Do NOT spread extractProfileData here — current state is already correct
                intervals: {
                    // Preserve user-level credentials when (re)creating self profile.
                    ...state.intervals,
                    athleteId: intervalsAthleteId,
                },
            };
        });

        return id;
    },

    removeProfile: (profileId) => set((state) => {
        const profile = state.profiles.find(p => p.id === profileId);
        if (!profile || profile.relation === 'self') return state;

        const profiles = state.profiles.filter(p => p.id !== profileId);
        let activeProfileId = state.activeProfileId;
        if (activeProfileId === profileId) {
            const selfProfile = profiles.find(p => p.relation === 'self');
            activeProfileId = selfProfile?.id || profiles[0]?.id || null;
        }

        if (activeProfileId && activeProfileId !== state.activeProfileId) {
            const newActive = profiles.find(p => p.id === activeProfileId);
            if (newActive) {
                const profileData = extractProfileData(newActive);
                return {
                    profiles,
                    activeProfileId,
                    ...profileData,
                    intervals: {
                        ...state.intervals,
                        athleteId: newActive.intervalsAthleteId,
                    },
                };
            }
        }

        const hasCoached = profiles.some(p => p.relation === 'coached');
        return {
            profiles,
            activeProfileId,
            userRole: hasCoached ? state.userRole : 'athlete',
        };
    }),

    async updateProfileDisplayName(profileId, displayName) {
        set((state) => ({
            profiles: state.profiles.map(p =>
                p.id === profileId ? { ...p, displayName } : p
            ),
            ...(state.activeProfileId === profileId ? {
                athlete: { ...state.athlete, name: displayName }
            } : {}),
        }));

        // IMMEDIATE SYNC
        try {
            const { uploadLocalDataToCloud } = await import('@/services/cloudSync');
            await uploadLocalDataToCloud({ includeProfile: true, includeSettings: true, includePlan: false });
        } catch (e) {
            log.error('updateDisplayName', 'Failed to auto-save profile name', { error: e instanceof Error ? e.message : String(e) });
        }
    },


    async updateProfileAthlete(profileId, athleteData) {
        const currentState = get();
        const targetProfile = currentState.profiles.find(p => p.id === profileId);
        const isCoached = targetProfile?.relation === 'coached';

        if (isCoached) {
            log.info('updateProfileAthlete', 'Updating coached profile', { profileId, fields: Object.keys(athleteData) });
        }

        set((state) => {
            return {
                profiles: state.profiles.map(p =>
                    p.id === profileId ? { ...p, athlete: { ...p.athlete, ...athleteData } } : p
                ),
                ...(state.activeProfileId === profileId ? {
                    athlete: { ...state.athlete, ...athleteData }
                } : {}),
            };
        });

        // IMMEDIATE SYNC: Trigger cloud upload to persist changes
        try {
            const { uploadLocalDataToCloud } = await import('@/services/cloudSync');
            log.debug('updateProfileAthlete', 'Triggering cloud sync for profile update', { relation: isCoached ? 'coached' : 'self' });
            await uploadLocalDataToCloud({ includeProfile: true, includeSettings: true, includePlan: false });
        } catch (e) {
            log.error('updateProfileAthlete', 'Failed to auto-save profile update', { error: e instanceof Error ? e.message : String(e) });
        }
    },

    checkAdminPromotion: (_email) => {
        // Deprecated: role promotion must only come from server-authoritative profile data.
    },

    regenerateInvite: (profileId) => {
        const token = crypto.randomUUID();
        set((state) => ({
            profiles: state.profiles.map(p =>
                p.id === profileId ? { ...p, inviteToken: token } : p
            )
        }));
        return token;
    },

    async updateProfileIntervalsId(profileId, intervalsId) {
        set((state) => ({
            profiles: state.profiles.map(p =>
                p.id === profileId ? { ...p, intervalsAthleteId: intervalsId } : p
            ),
            ...(state.activeProfileId === profileId ? {
                intervals: { ...state.intervals, athleteId: intervalsId }
            } : {}),
        }));

        // IMMEDIATE SYNC
        try {
            const { uploadLocalDataToCloud } = await import('@/services/cloudSync');
            await uploadLocalDataToCloud({ includeProfile: true, includeSettings: true, includePlan: false });
        } catch (e) {
            log.error('updateIntervalsId', 'Failed to auto-save intervals ID', { error: e instanceof Error ? e.message : String(e) });
        }
    },
});
